import { useInfiniteQuery, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ApiError, BrowseGroup, BrowseTrack } from "@touchscreen/api-client";
import { useTouchscreenContext } from "./TouchscreenProvider.js";
import { touchscreenKeys } from "./queryKeys.js";

/** Groups only, per touchscreen-api.md — never load every track of every compilation up front. */
export function useBrowseGroups(): UseQueryResult<BrowseGroup[], ApiError> {
  const { client, venue } = useTouchscreenContext();
  return useQuery({
    queryKey: touchscreenKeys.browseGroups(venue?.ref ?? ""),
    queryFn: () => client.browse(),
    enabled: !!venue,
  });
}

export interface BrowseGroupTracksState {
  tracks: BrowseTrack[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: boolean;
  loadMore: () => void;
}

/**
 * Paginated tracks within one group — "load more" is `fetchNextPage`, and `tracks` is every
 * loaded page flattened, matching the accumulating list the kiosk/phone "load more" button
 * expects.
 */
export function useBrowseGroupTracks(groupId: number | null, perPage: number): BrowseGroupTracksState {
  const { client, venue } = useTouchscreenContext();

  const query = useInfiniteQuery({
    queryKey: touchscreenKeys.browseGroupTracks(venue?.ref ?? "", groupId ?? -1),
    queryFn: ({ pageParam }) => client.browseGroupTracks(groupId as number, { page: pageParam, perPage }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.items.length === perPage ? allPages.length + 1 : undefined),
    enabled: !!venue && groupId !== null,
  });

  return {
    tracks: query.data ? query.data.pages.flatMap((page) => page.items) : [],
    loading: query.isLoading,
    loadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage ?? false,
    error: query.isError,
    loadMore: () => void query.fetchNextPage(),
  };
}
